/**
 * useMenuAddEvent — the site header's "Add Event" for the V2 calendars (HW-1038).
 *
 * The header link (top-menu-add-event.js) points at the page you are on plus
 * the legacy hash `#/!?…&action=event-add&…`. On a calendar page that is a
 * same-document hash change: nothing reloads, so reading the hash once on mount
 * missed every click after the page had loaded (Week only worked because it
 * rewrites its path after load, which turned the stale link into a reload).
 *
 * The hook reads the hash on mount AND on every `hashchange`, opens the page's
 * create modal once the page is `ready`, then scrubs the hash so a reload or
 * back-nav doesn't reopen it and the next click changes the hash again.
 *
 * @param {Function} open Opens the page's create modal (same as its own
 *   "Create Event" button).
 * @param {boolean} ready The page can open it now (data loaded, user may create).
 */
import { useEffect, useRef, useState } from 'react';

function hasAddEventHash() {
	if (typeof window === 'undefined') return false;
	const hash = (window.location.hash || '').replace(/^#\/?!?\??/, '');
	return hash.split('&').some(kv => kv === 'action=event-add');
}

function scrubHash() {
	try {
		window.history.replaceState(null, '', window.location.pathname + window.location.search);
	} catch (_e) {
		/* non-fatal */
	}
}

export default function useMenuAddEvent(open, ready) {
	const [pending, setPending] = useState(hasAddEventHash);
	const openRef = useRef(open);
	openRef.current = open;

	useEffect(() => {
		const onHashChange = () => {
			if (hasAddEventHash()) setPending(true);
		};
		window.addEventListener('hashchange', onHashChange);
		return () => window.removeEventListener('hashchange', onHashChange);
	}, []);

	useEffect(() => {
		if (!pending || !ready) return;
		setPending(false);
		scrubHash();
		openRef.current();
	}, [pending, ready]);
}
